import React, { useContext, useEffect, useState } from 'react';
import { Alert } from '@syneto/compass-react';
import { Link } from 'react-router-dom';
import { StoreContext } from '../../../../contexts/StoreContext';
import { ServiceContext } from '../../../../contexts/ServiceContext';
import { BANNER_KEYS } from '../../../../services/BannerDismissal';
import { HideBannerModal } from './HideBannerModal';

const INVENTORY_PATH = '/inventory';

// The URL is typed by a Syneto admin and lands in an href, so only web links are rendered; anything
// else (a javascript: URL, a bare word) is treated like an unset value.
const isWebUrl = (value) => {
	return /^https?:\/\//i.test(value);
};

// SYN-3072 / SYN-3176: the invitation to join the Partner Preview Program. Shown only to a user whose
// company is NOT enrolled (no participation, a disabled participation, or an access request still
// pending) and who has not dismissed it; the dismissal is permanent and stored server-side per user
// under its own banner key. Once the company is enrolled the invitation never shows, whether or not
// the enrolment banner was dismissed: it would ask the user to join something they are already in.
// The enrolment status comes from the participation, the same read PartnerPreviewBanner uses. If that
// read fails the invitation stays hidden: we cannot tell whether the company is enrolled, and
// inviting an enrolled company is exactly what this rule prevents. (PartnerPreviewService reads a 404
// as "no participation"; rest-api answers this list endpoint with [] rather than 404.) It does not
// read the access request: a pending request is not enrolled, so the invitation behaves as before.
export const PartnerPreviewInvitationBanner = () => {
	const { bannerDismissalService, settingsService, accessService, partnerPreviewService } = useContext(ServiceContext);
	const { state } = useContext(StoreContext);
	const { loggedInUser } = state;
	const canAccessInventory = accessService.hasPermissionOnAnyResource('appliance.list');

	const [visible, setVisible] = useState(false);
	const [learnMoreUrl, setLearnMoreUrl] = useState('');
	const [confirming, setConfirming] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		const loadBannerState = async () => {
			// Read independently: a failed settings read only costs the Learn More link, while a
			// failed dismissals or participation read hides the banner - showing it again to someone
			// who already dismissed it, or to a company we cannot confirm is not enrolled, would be
			// worse than not showing it.
			const [dismissalsResult, participationResult, learnMoreUrlResult] = await Promise.allSettled([
				bannerDismissalService.getDismissals(loggedInUser.company.id),
				partnerPreviewService.getParticipation(loggedInUser.company.id),
				settingsService.getPartnerPreviewLearnMoreUrl(),
			]);

			if (dismissalsResult.status === 'fulfilled' && participationResult.status === 'fulfilled') {
				// dismissals contains every banner the user has dismissed, not just this one.
				const dismissed = dismissalsResult.value.some((dismissal) => {
					return dismissal.bannerKey === BANNER_KEYS.PARTNER_PREVIEW_INVITATION;
				});
				const enrolled = Boolean(participationResult.value?.enabled);

				setVisible(!enrolled && !dismissed);
			}

			if (learnMoreUrlResult.status === 'fulfilled' && isWebUrl(learnMoreUrlResult.value)) {
				setLearnMoreUrl(learnMoreUrlResult.value);
			}
		};

		loadBannerState();
	}, []);

	const handleConfirmHide = async () => {
		setSubmitting(true);

		try {
			await bannerDismissalService.dismissBanner(loggedInUser.company.id, BANNER_KEYS.PARTNER_PREVIEW_INVITATION);
			setVisible(false);
		} catch (error) {
			// Keep the banner up: hiding it would suggest a dismissal that was never persisted,
			// so it would come back on the next login anyway. See PartnerPreviewBanner.
			setConfirming(false);
			setSubmitting(false);
		}
	};

	if (!visible) {
		return null;
	}

	return (
		<>
			<Alert variant="primary" className="mb-3" dismissible closeLabel="Close" onClose={() => { return setConfirming(true); }}>
				<div className="d-flex justify-content-between align-items-center">
					<div>
						<div className="fw-bold">Access the Partner Preview Program.</div>
						<div>Get early access to SynetoOS Preview builds and help us validate new releases before General Availability.</div>
					</div>
					<div className="d-flex align-items-center flex-shrink-0 text-nowrap ms-4">
						{canAccessInventory && (
							/* Navigation is a link, not a button - see PartnerPreviewBanner. */
							<Link to={INVENTORY_PATH} className="btn btn-primary me-2">Go to Inventory</Link>
						)}
						{learnMoreUrl && (
							<a href={learnMoreUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary">Learn More</a>
						)}
					</div>
				</div>
			</Alert>
			<HideBannerModal
				show={confirming}
				submitting={submitting}
				onHide={() => { return setConfirming(false); }}
				onConfirm={handleConfirmHide}
			/>
		</>
	);
};
